/**
 * The sidebar tree, resolved from permissions and the user's own arrangement.
 *
 * Lives in a hook rather than in `MainLayout` because two screens need the same
 * tree: the sidebar that draws it, and the Customize Navigation page that
 * rearranges it. The page renders inside the layout as a route, so passing the
 * tree down would mean threading it through the router; both just call this.
 *
 * The ARRANGEMENT comes from the store, not from here. That is what makes a
 * save show up at once: both callers select the same slice, so the sidebar
 * re-renders on the same dispatch that persists the change.
 */
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { hasAnyPermissionForJob } from '../../constants/permissions';
import {
  ALL_COLLECTION_IDS,
  COLLECTION_JOBS,
  JOB_TO_COLLECTION,
  buildNav,
  normalizeJobName,
  type NavGroup,
} from './nav-model';
import { orderNav } from './use-nav-order';
import { hydrateNavOrder } from '../../features/navigation/navSlice';
import type { NavOrder } from './use-nav-order';

/** Stable empty default, so an absent permissions array keeps one identity. */
const NO_KEYS: string[] = [];

/** Stable default for the arrangement, for the same reason. */
const EMPTY: NavOrder = { groups: [], collections: {}, items: {} };

interface NavRootState {
  nav?: { order: NavOrder; saving: boolean; error: string | null };
}

/** The slice of the permissions payload this hook actually reads. */
interface PermissionJob {
  jobName: string;
  isMenu?: boolean;
}
interface PermissionModule {
  moduleName: string;
  jobs?: PermissionJob[];
}
interface AuthState {
  auth?: {
    user?: { _id?: string; id?: string; email?: string };
    permissions?: string[];
    permissionsData?: {
      currentModuleJobs?: { jobs?: PermissionJob[] };
      permissions?: PermissionModule[];
    };
  };
}

/**
 * True if the user can open a collection's screen — any permission on any of
 * the jobs that govern it. Unchanged in meaning from the flat menu it replaced.
 */
const canShowCollection = (id: string, userKeys: Set<string>): boolean => {
  const jobs = COLLECTION_JOBS[id];
  if (!jobs) return true;
  return jobs.some((job) => hasAnyPermissionForJob(job, userKeys));
};

export const useNav = () => {
  const dispatch = useDispatch();
  const authUser = useSelector((s: AuthState) => s.auth?.user);
  const permissionsData = useSelector((s: AuthState) => s.auth?.permissionsData);
  const currentModuleJobs = permissionsData?.currentModuleJobs;
  // NO_KEYS is a module constant, not `|| []`: a fresh array literal here would
  // be a new reference on every render and re-run the memo below each time.
  const permissionKeys = useSelector((s: AuthState) => s.auth?.permissions ?? NO_KEYS);

  /**
   * Which collections this user may open. Unchanged from the flat menu: the
   * same permission-driven fold, the same fallback when the payload is absent.
   */
  const nav: NavGroup[] = useMemo(() => {
    let jobs: PermissionJob[] = [];

    if (currentModuleJobs?.jobs && currentModuleJobs.jobs.length > 0) {
      jobs = currentModuleJobs.jobs;
    } else if (permissionsData?.permissions) {
      const coreModule = permissionsData.permissions.find(
        (module) => module.moduleName.toLowerCase() === 'core'
      );
      if (coreModule?.jobs) {
        jobs = coreModule.jobs;
      }
    }

    const visibleIds = new Set<string>();
    if (jobs.length > 0) {
      jobs.forEach((job) => {
        if (job.isMenu === false) return;
        const normalized = normalizeJobName(job.jobName);
        // A job can govern more than one screen — the feedback jobs open both
        // the Feedback module and the AI Feedback screen — so the mapping may be
        // a list. Unmapped jobs still fall back to their own name as the id.
        const mapped = JOB_TO_COLLECTION[normalized] || normalized;
        (Array.isArray(mapped) ? mapped : [mapped]).forEach((id) => {
          if (ALL_COLLECTION_IDS.includes(id)) visibleIds.add(id);
        });
      });
      // Always surface Access Control (RBAC) for admins, as before.
      visibleIds.add('accesscontrol');
    } else {
      const userKeys = new Set<string>(permissionKeys);
      ALL_COLLECTION_IDS.forEach((id) => {
        if (canShowCollection(id, userKeys)) visibleIds.add(id);
      });
    }

    return buildNav(visibleIds, permissionKeys);
  }, [currentModuleJobs, permissionsData, permissionKeys]);

  // Per user, so one person's arrangement is never shown to the next on a
  // shared machine.
  const userId = authUser?._id || authUser?.id || authUser?.email || '';
  const order = useSelector((s: NavRootState) => s.nav?.order) ?? EMPTY;
  const saving = useSelector((s: NavRootState) => s.nav?.saving) ?? false;
  const error = useSelector((s: NavRootState) => s.nav?.error) ?? null;

  // Read this user's saved arrangement into the store once. The reducer is a
  // no-op when the id has not changed, so this is safe to run on every render
  // and still re-reads when the account does.
  useEffect(() => {
    dispatch(hydrateNavOrder(userId));
  }, [dispatch, userId]);

  const orderedNav = useMemo(() => orderNav(nav, order), [nav, order]);

  return { nav: orderedNav, defaultNav: nav, order, userId, saving, error };
};
